/**
 * Hover utilities for crosshair and tooltip positioning.
 * Pure functions for hover event building and interpolation - no React dependencies.
 */

import { binarySearchNearestTimestamp } from "./hitTesting";
import type {
  HoverEvent,
  OhlcvPoint,
  PricePoint,
  Scale,
  TickerSeries,
} from "~types/types";

export interface HoverStateInput {
  screenX: number;
  screenY: number;
  chartX: number;
  chartY: number;
  timestamp: number | null;
  nearestPoints: Map<string, PricePoint | OhlcvPoint | null>;
}

export interface InterpolationResult {
  y: number | null;
  value: number | null;
}

/**
 * Build HoverEvent from store state for tooltip callbacks.
 */
export function buildHoverEvent(
  mode: "single" | "comparison",
  hover: HoverStateInput,
  seriesMeta: Map<string, TickerSeries>,
  dataPointTimestamp: number
): HoverEvent | null {
  if (mode === "single") {
    const firstSeriesId = Array.from(hover.nearestPoints.keys())[0];

    if (!firstSeriesId) return null;

    const series = seriesMeta.get(firstSeriesId);
    const datum = hover.nearestPoints.get(firstSeriesId) ?? null;

    if (!series) return null;

    return {
      mode: "single",
      screenX: hover.screenX,
      screenY: hover.screenY,
      chartX: hover.chartX,
      chartY: hover.chartY,
      timestamp: dataPointTimestamp,
      series,
      datum,
    };
  }

  // Comparison mode
  const seriesPoints: Record<string, PricePoint | OhlcvPoint | null> = {};
  const seriesMetaRecord: Record<string, TickerSeries> = {};

  for (const [seriesId, point] of hover.nearestPoints) {
    const series = seriesMeta.get(seriesId);

    if (series) {
      seriesPoints[seriesId] = point;
      seriesMetaRecord[seriesId] = series;
    }
  }

  return {
    mode: "comparison",
    screenX: hover.screenX,
    screenY: hover.screenY,
    chartX: hover.chartX,
    chartY: hover.chartY,
    timestamp: dataPointTimestamp,
    seriesPoints,
    seriesMeta: seriesMetaRecord,
  };
}

/**
 * Interpolate Y value at a given timestamp using linear interpolation between data points.
 * Used for positioning crosshair dots on the line path.
 */
interface BracketResult {
  leftPoint: PricePoint | undefined;
  rightPoint: PricePoint | undefined;
  foundBracket: boolean;
  exactMatch: PricePoint | undefined;
}

function isPricePoint(p: PricePoint | OhlcvPoint): p is PricePoint {
  return "value" in p;
}

const NO_BRACKET: BracketResult = {
  leftPoint: undefined,
  rightPoint: undefined,
  foundBracket: false,
  exactMatch: undefined,
};

function scanForPricePoint(
  data: (PricePoint | OhlcvPoint)[],
  start: number,
  step: 1 | -1,
  predicate: (p: PricePoint) => boolean
): PricePoint | undefined {
  for (let i = start; step === 1 ? i < data.length : i >= 0; i += step) {
    const p = data[i];

    if (p && isPricePoint(p) && predicate(p)) return p;
  }

  return undefined;
}

function findBracketPoints(
  visibleData: (PricePoint | OhlcvPoint)[],
  targetTimestamp: number
): BracketResult {
  if (visibleData.length === 0) return NO_BRACKET;

  const nearestIndex = binarySearchNearestTimestamp(
    visibleData,
    targetTimestamp
  );

  if (nearestIndex < 0 || nearestIndex >= visibleData.length) return NO_BRACKET;

  const nearest = visibleData[nearestIndex];

  if (!nearest) return NO_BRACKET;

  if (nearest.timestamp === targetTimestamp && isPricePoint(nearest)) {
    return { ...NO_BRACKET, exactMatch: nearest };
  }

  const startLeft =
    nearest.timestamp <= targetTimestamp ? nearestIndex : nearestIndex - 1;
  const startRight =
    nearest.timestamp > targetTimestamp ? nearestIndex : nearestIndex + 1;

  const leftPoint = scanForPricePoint(
    visibleData,
    startLeft,
    -1,
    (p) => p.timestamp <= targetTimestamp
  );
  const rightPoint = scanForPricePoint(
    visibleData,
    startRight,
    1,
    (p) => p.timestamp > targetTimestamp
  );

  const foundBracket = leftPoint !== undefined && rightPoint !== undefined;

  return { leftPoint, rightPoint, foundBracket, exactMatch: undefined };
}

export function interpolateYPosition(
  visibleData: (PricePoint | OhlcvPoint)[],
  targetTimestamp: number,
  yScale: Scale
): InterpolationResult {
  if (!visibleData.length) return { y: null, value: null };

  const { leftPoint, rightPoint, foundBracket, exactMatch } = findBracketPoints(
    visibleData,
    targetTimestamp
  );

  if (exactMatch) {
    return { y: yScale.scale(exactMatch.value), value: exactMatch.value };
  }

  if (!leftPoint) return { y: null, value: null };

  if (!foundBracket || !rightPoint) {
    return { y: yScale.scale(leftPoint.value), value: leftPoint.value };
  }

  const t1 = leftPoint.timestamp;
  const t2 = rightPoint.timestamp;
  const y1 = leftPoint.value;
  const y2 = rightPoint.value;

  if (t2 === t1) return { y: yScale.scale(y1), value: y1 };

  const t = (targetTimestamp - t1) / (t2 - t1);
  const interpolatedValue = y1 + (y2 - y1) * t;

  return {
    y: yScale.scale(interpolatedValue),
    value: interpolatedValue,
  };
}

/**
 * Calculate dot Y position for OHLC/candlestick charts.
 * Clamps cursor Y to the candle's high/low range.
 */
export function calculateCandlestickDotY(
  point: OhlcvPoint,
  cursorY: number,
  yScale: Scale
): number {
  // Get cursor Y position and convert to price
  const cursorPrice = yScale.invert(cursorY);

  // Clamp to candle's high/low range
  const clampedPrice = Math.max(point.low, Math.min(point.high, cursorPrice));

  return yScale.scale(clampedPrice);
}

export interface TooltipPosition {
  x: number;
  y: number;
}

export interface TooltipPositionOptions {
  snappedX: number;
  chartLeft: number;
  chartRight: number;
  chartTop: number;
  chartHeight: number;
  tooltipWidth: number;
  tooltipHeight: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Calculate tooltip position, switching sides when near edges.
 */
export function calculateTooltipPosition(
  options: TooltipPositionOptions
): TooltipPosition {
  const {
    snappedX,
    chartLeft,
    chartRight,
    chartTop,
    chartHeight,
    tooltipWidth,
    tooltipHeight,
    offsetX,
    offsetY,
  } = options;

  // Determine if tooltip should be on left or right of crosshair
  const spaceOnRight = chartRight - snappedX;
  const spaceOnLeft = snappedX - chartLeft;

  let x: number;

  if (spaceOnRight >= tooltipWidth + offsetX) {
    // Enough space on right - position to the right of crosshair
    x = snappedX + offsetX;
  } else if (spaceOnLeft >= tooltipWidth + offsetX) {
    // Position to the left of crosshair
    x = snappedX - tooltipWidth - offsetX;
  } else {
    // Not enough space on either side - center it
    x = Math.max(
      chartLeft,
      Math.min(snappedX - tooltipWidth / 2, chartRight - tooltipWidth)
    );
  }

  // Position tooltip near the top of the chart area
  let y = chartTop + offsetY;

  // Ensure tooltip stays within chart bounds vertically
  if (y + tooltipHeight > chartTop + chartHeight) {
    y = chartTop + chartHeight - tooltipHeight - offsetY;
  }

  return { x, y };
}
